import React, { useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectItem,
  Card,
  CardBody,
  Textarea,
  Switch,
  Chip,
} from '@heroui/react';
import { Plus, Trash, ArrowUp, ArrowDown, X, Link as LinkIcon } from '@phosphor-icons/react';
import { FieldDefinition, FieldType, FileLogConnection } from 'shared/types/asset';
import { useFileLabels } from 'hooks/useAssets';

interface FieldDefinitionBuilderProps {
  fields: FieldDefinition[];
  onChange: (fields: FieldDefinition[]) => void;
  className?: string;
  categoryId?: string | number; // Optional category ID for filtering file labels
  availableFileLabels?: string[]; // Optional list of available file labels for this category
}

export const FieldDefinitionBuilder: React.FC<FieldDefinitionBuilderProps> = ({
  fields,
  onChange,
  className = '',
  availableFileLabels,
}) => {
  const [expandedField, setExpandedField] = useState<string | null>(null);

  // Fetch all file labels (only used as fallback if no availableFileLabels provided)
  const { fileLabels: allFileLabels, isLoading: isLoadingLabels } = useFileLabels();

  // Use provided labels if available, otherwise use all labels
  const fileLabels = availableFileLabels?.length
    ? availableFileLabels.map(label => ({ label, id: label }))
    : allFileLabels;

  const fieldTypes: Array<{ value: FieldType; label: string }> = [
    { value: FieldType.STRING, label: 'Text' },
    { value: FieldType.NUMBER, label: 'Number' },
    { value: FieldType.BOOLEAN, label: 'Yes/No' },
    { value: FieldType.DATE, label: 'Date' },
    { value: FieldType.ENUM, label: 'Dropdown (Single)' },
    { value: FieldType.MULTI_ENUM, label: 'Dropdown (Multiple)' },
    { value: FieldType.FILE_REFERENCE, label: 'File Reference' },
  ];

  const handleAddField = () => {
    const newField: FieldDefinition = {
      fieldName: `field_${Date.now()}`,
      displayName: 'New Field',
      fieldType: FieldType.STRING,
      displayOrder: fields.length,
      validationRules: {},
      metadata: {},
    };

    onChange([...fields, newField]);
    setExpandedField(newField.fieldName);
  };

  const handleUpdateField = (index: number, updates: Partial<FieldDefinition>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    onChange(newFields);
  };

  const handleRemoveField = (index: number) => {
    const field = fields[index];
    const fieldName = field.displayName || 'dit veld';

    if (!confirm(`Weet je zeker dat je "${fieldName}" wilt verwijderen?`)) {
      return;
    }

    const newFields = fields.filter((_, i) => i !== index);
    // Reorder remaining fields
    newFields.forEach((field, i) => {
      field.displayOrder = i;
    });
    onChange(newFields);
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === fields.length - 1)
    ) {
      return;
    }

    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Swap fields
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];

    // Update display order
    newFields.forEach((field, i) => {
      field.displayOrder = i;
    });

    onChange(newFields);
  };

  const handleAddEnumValue = (fieldIndex: number) => {
    const field = fields[fieldIndex];
    const newEnumValues = [...(field.enumValues || []), ''];
    handleUpdateField(fieldIndex, { enumValues: newEnumValues });
  };

  const handleUpdateEnumValue = (fieldIndex: number, enumIndex: number, value: string) => {
    const field = fields[fieldIndex];
    const newEnumValues = [...(field.enumValues || [])];
    newEnumValues[enumIndex] = value;
    handleUpdateField(fieldIndex, { enumValues: newEnumValues });
  };

  const handleRemoveEnumValue = (fieldIndex: number, enumIndex: number) => {
    const field = fields[fieldIndex];
    const newEnumValues = (field.enumValues || []).filter((_, i) => i !== enumIndex);
    handleUpdateField(fieldIndex, { enumValues: newEnumValues });
  };

  const renderFieldEditor = (field: FieldDefinition, index: number) => {
    const isExpanded = expandedField === field.fieldName;
    const isEnumType = field.fieldType === FieldType.ENUM || field.fieldType === FieldType.MULTI_ENUM;

    return (
      <Card key={field.fieldName} className="mb-3">
        <CardBody className="p-4">
          {/* Field Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="flex flex-col gap-1">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => handleMoveField(index, 'up')}
                  isDisabled={index === 0}
                >
                  <ArrowUp size={16} />
                </Button>
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => handleMoveField(index, 'down')}
                  isDisabled={index === fields.length - 1}
                >
                  <ArrowDown size={16} />
                </Button>
              </div>

              <div className="flex-1">
                <Input
                  label="Field Name"
                  value={field.displayName}
                  onChange={(e) => handleUpdateField(index, { displayName: e.target.value })}
                  size="sm"
                />
              </div>

              <div className="w-48">
                <Select
                  label="Type"
                  selectedKeys={[field.fieldType]}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0] as string;
                    handleUpdateField(index, { fieldType: selectedKey as FieldType });
                  }}
                  size="sm"
                >
                  {fieldTypes.map((type) => (
                    <SelectItem key={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>

              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => setExpandedField(isExpanded ? null : field.fieldName)}
              >
                {isExpanded ? <X size={20} /> : '+'}
              </Button>

              <Button
                isIconOnly
                size="sm"
                color="danger"
                variant="light"
                onPress={() => handleRemoveField(index)}
              >
                <Trash size={18} />
              </Button>
            </div>
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 space-y-3 border-t pt-4">
              <Input
                label="Field Name (Internal)"
                value={field.fieldName}
                onChange={(e) => handleUpdateField(index, { fieldName: e.target.value })}
                description="Unique identifier for this field (no spaces)"
                size="sm"
              />

              {/* Validation Rules */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Validation Rules</div>
                <div className="grid grid-cols-2 gap-3">
                  <Switch
                    isSelected={field.isRequired || false}
                    onValueChange={(checked) =>
                      handleUpdateField(index, {
                        isRequired: checked,
                      })
                    }
                  >
                    Required
                  </Switch>

                  {field.fieldType === FieldType.NUMBER && (
                    <>
                      <Input
                        type="number"
                        label="Min Value"
                        value={field.validationRules?.min?.toString() || ''}
                        onChange={(e) =>
                          handleUpdateField(index, {
                            validationRules: {
                              ...field.validationRules,
                              min: parseFloat(e.target.value),
                            },
                          })
                        }
                        size="sm"
                      />
                      <Input
                        type="number"
                        label="Max Value"
                        value={field.validationRules?.max?.toString() || ''}
                        onChange={(e) =>
                          handleUpdateField(index, {
                            validationRules: {
                              ...field.validationRules,
                              max: parseFloat(e.target.value),
                            },
                          })
                        }
                        size="sm"
                      />
                    </>
                  )}

                  {field.fieldType === FieldType.STRING && (
                    <Input
                      label="Pattern (Regex)"
                      value={field.validationRules?.pattern || ''}
                      onChange={(e) =>
                        handleUpdateField(index, {
                          validationRules: { ...field.validationRules, pattern: e.target.value },
                        })
                      }
                      size="sm"
                    />
                  )}
                </div>
              </div>

              {/* Enum Values */}
              {isEnumType && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Dropdown Options</div>
                    <Button
                      size="sm"
                      color="primary"
                      variant="flat"
                      onPress={() => handleAddEnumValue(index)}
                      startContent={<Plus size={16} />}
                    >
                      Add Option
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(field.enumValues || []).map((enumValue, enumIndex) => (
                      <div key={enumIndex} className="flex items-center gap-2">
                        <Input
                          value={enumValue}
                          onChange={(e) => handleUpdateEnumValue(index, enumIndex, e.target.value)}
                          placeholder="Option value"
                          size="sm"
                        />
                        <Button
                          isIconOnly
                          size="sm"
                          color="danger"
                          variant="light"
                          onPress={() => handleRemoveEnumValue(index, enumIndex)}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Field Metadata</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Unit (e.g., m², kg)"
                    value={field.metadata?.unit || ''}
                    onChange={(e) =>
                      handleUpdateField(index, {
                        metadata: { ...field.metadata, unit: e.target.value },
                      })
                    }
                    size="sm"
                  />
                  <Input
                    label="Placeholder"
                    value={field.metadata?.placeholder || ''}
                    onChange={(e) =>
                      handleUpdateField(index, {
                        metadata: { ...field.metadata, placeholder: e.target.value },
                      })
                    }
                    size="sm"
                  />
                </div>
                <Textarea
                  label="Help Text"
                  value={field.metadata?.helpText || ''}
                  onChange={(e) =>
                    handleUpdateField(index, {
                      metadata: { ...field.metadata, helpText: e.target.value },
                    })
                  }
                  size="sm"
                  rows={2}
                />
              </div>

              {/* File-Log Connection */}
              {(field.fieldType === FieldType.DATE || field.fieldType === FieldType.STRING || field.fieldType === FieldType.NUMBER) && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <LinkIcon size={16} className="text-gray-600" />
                      <div className="text-sm font-medium text-gray-700">Bestandslog Koppeling</div>
                      {field.fileLogConnection?.enabled && (
                        <Chip size="sm" color="warning" variant="flat">
                          VERBONDEN
                        </Chip>
                      )}
                    </div>
                    <Switch
                      isSelected={field.fileLogConnection?.enabled || false}
                      onValueChange={(checked) =>
                        handleUpdateField(index, {
                          fileLogConnection: checked
                            ? {
                                enabled: true,
                                sourceField: 'documentDate',
                                priorityStrategy: 'NEWEST_DOCUMENT_DATE',
                              }
                            : undefined,
                        })
                      }
                      size="sm"
                    >
                      Verbind met bestandslog
                    </Switch>
                  </div>

                  {field.fileLogConnection?.enabled && (
                    <>
                      <div className="bg-orange-50 border border-orange-200 rounded-md p-3">
                        <p className="text-xs text-orange-800">
                          ⚠️ Dit veld wordt automatisch gevuld vanuit gekoppelde documenten en is <strong>alleen-lezen</strong> in de tabel.
                          Wijzigingen moeten via het document zelf worden gemaakt.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            Bronveld in Bestandslog
                          </label>
                          <Select
                            selectedKeys={field.fileLogConnection.sourceField ? [field.fileLogConnection.sourceField] : []}
                            onSelectionChange={(keys) => {
                              const selectedKey = Array.from(keys)[0] as string;
                              handleUpdateField(index, {
                                fileLogConnection: {
                                  ...field.fileLogConnection!,
                                  sourceField: selectedKey as FileLogConnection['sourceField'],
                                },
                              });
                            }}
                            size="sm"
                            aria-label="Bronveld selecteren"
                          >
                            <SelectItem key="documentDate">
                              Document Datum (documentDate)
                            </SelectItem>
                            <SelectItem key="expirationDate">
                              Verloopdatum (expirationDate)
                            </SelectItem>
                            <SelectItem key="createdAt">
                              Upload Datum (createdAt)
                            </SelectItem>
                          </Select>
                        </div>

                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">
                            Prioriteitsstrategie
                          </label>
                          <Select
                            selectedKeys={field.fileLogConnection.priorityStrategy ? [field.fileLogConnection.priorityStrategy] : []}
                            onSelectionChange={(keys) => {
                              const selectedKey = Array.from(keys)[0] as string;
                              handleUpdateField(index, {
                                fileLogConnection: {
                                  ...field.fileLogConnection!,
                                  priorityStrategy: selectedKey as FileLogConnection['priorityStrategy'],
                                },
                              });
                            }}
                            size="sm"
                            aria-label="Prioriteitsstrategie selecteren"
                          >
                            <SelectItem key="NEWEST_CREATED">
                              Nieuwste Upload
                            </SelectItem>
                            <SelectItem key="OLDEST_CREATED">
                              Oudste Upload
                            </SelectItem>
                            <SelectItem key="NEWEST_DOCUMENT_DATE">
                              Nieuwste Document Datum
                            </SelectItem>
                            <SelectItem key="OLDEST_DOCUMENT_DATE">
                              Oudste Document Datum
                            </SelectItem>
                            <SelectItem key="NEWEST_EXPIRATION">
                              Nieuwste Verloopdatum
                            </SelectItem>
                            <SelectItem key="OLDEST_EXPIRATION">
                              Oudste Verloopdatum
                            </SelectItem>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">
                          Bestandslabel Filter (optioneel)
                        </label>
                        <Select
                          selectionMode="multiple"
                          placeholder="Selecteer bestandslabels..."
                          selectedKeys={new Set(field.fileLogConnection.fileLabelFilter || [])}
                          onSelectionChange={(keys) => {
                            const selectedLabels = Array.from(keys) as string[];
                            handleUpdateField(index, {
                              fileLogConnection: {
                                ...field.fileLogConnection!,
                                fileLabelFilter: selectedLabels.length > 0 ? selectedLabels : undefined,
                              },
                            });
                          }}
                          size="sm"
                          isLoading={!availableFileLabels && isLoadingLabels}
                          aria-label="Selecteer bestandslabels"
                          classNames={{
                            trigger: 'min-h-[40px]',
                          }}
                          isDisabled={fileLabels.length === 0}
                        >
                          {fileLabels.map((label) => (
                            <SelectItem key={typeof label === 'string' ? label : label.label}>
                              {typeof label === 'string' ? label : label.label}
                            </SelectItem>
                          ))}
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          {fileLabels.length === 0
                            ? 'Voeg eerst bestandsvereisten toe in de tab "Bestandsvereisten"'
                            : 'Als leeg gelaten, worden alle bestandslabels meegenomen'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Field Definitions</h3>
        <Button
          color="primary"
          size="sm"
          onPress={handleAddField}
          startContent={<Plus size={16} />}
        >
          Add Field
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500 text-sm">No fields defined yet</p>
          <p className="text-gray-400 text-xs mt-1">
            Click &quot;Add Field&quot; to create custom fields for this asset category
          </p>
        </div>
      ) : (
        <div className="space-y-0">
          {fields
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((field, index) => renderFieldEditor(field, index))}
        </div>
      )}
    </div>
  );
};
